/**
 * User Management API Routes
 * Direct API service for all user management operations
 * Following the pattern from locationApi.ts
 */

import { createApiClient } from '../../../../api/apiClient';
import store from '../../../../store';
import { User, UserUpdateRequest, UserUpdatePayload } from './userManagementTypes';

// Paginated backend response wrapper
interface BackendPagedResponse {
  data?: BackendUserResponse[];        // most common wrapper key
  content?: BackendUserResponse[];     // Spring-style wrapper key
  hasNext?: boolean;                   // true when more pages exist
  totalCount?: number;                 // total record count
  totalPages?: number;                 // total page count
  activeCount?: number;                // count of unique active users (from backend)
  newUserCount?: number;               // count of unique new users (from backend)
}

// Backend response interface
// Primary keys match the paginated list endpoint; optional alternatives cover the
// single-user detail endpoint which may use different field names.
interface BackendUserResponse {
  // ── Primary keys (paginated list) ────────────────────────────────────────
  id: number;
  userEmail: string;
  gpid: string;
  role: string;
  organization?: string;
  department?: string;
  locations: Array<{
    location: string;
    locationId: string;
    activeStatus: boolean;
  }>;
  locationMappings?: Array<{
    scopeType?: string | null;
    scopeRegion?: string | null;
    scopeMarket?: string | null;
    locationId?: number | string | null;
    location?: string | null;
    locationCode?: string | null;
    locationSapId?: string | null;
    locationName?: string | null;
    locationType?: string | null;
    organizationType?: string | null;
    region?: string | null;
    market?: string | null;
    division?: string | null;
    roleId?: number | null;
    role?: string | null;
    liveFlag?: string | null;
    sustainTeamMember?: boolean | null;
    fundamentals?: string | null;
    lighthouse?: string | null;
    advFundamentals?: string | null;
  }>;
  lastUpdateTime: string;
  naviJoinDate: string | null;
  sustainTeamMember: boolean | null;
  sustainTeamRole?: 'Member' | 'Lead' | null;
  fundamentals: string | null;
  lighthouse: string | null;
  advFundamentals: string | null;
  newStatus: boolean | null;
  activeStatus?: boolean | null;   // top-level flag returned by both list and detail endpoints
  scopeType?: string | null;       // top-level GLOBAL/REGION/MARKET/SITE flag (list endpoint)

  // ── Alternative keys (single-user / detail endpoint) ──────────────────
  userId?: number;        // alternative for id
  email?: string;         // alternative for userEmail
  roleName?: string;      // alternative for role
  lastUpdated?: string;   // alternative for lastUpdateTime
  updatedAt?: string;     // alternative for lastUpdateTime
  joinDate?: string;      // alternative for naviJoinDate
  createdAt?: string;     // alternative for naviJoinDate
}

// API base path (without /api prefix - handled by createApiClient)
const API_BASE = '/admin/users';

// Authenticated axios instance — interceptor auto-injects Bearer token on every request
const api = createApiClient();

/**
 * Transform backend user DTO to frontend User model
 */
function mapApiToUser(apiUser: BackendUserResponse, index: number): User {
  const primaryMapping = apiUser.locationMappings?.[0];
  // Normalise id — list endpoint uses `id`, detail endpoint may use `userId`
  const numericId = apiUser.id ?? apiUser.userId;
  // Normalise lastUpdateTime — detail endpoint may use lastUpdated or updatedAt
  const lastUpdate = apiUser.lastUpdateTime ?? apiUser.lastUpdated ?? apiUser.updatedAt ?? '';
  // Normalise naviJoinDate — detail endpoint may use joinDate or createdAt
  const joinDate = apiUser.naviJoinDate ?? apiUser.joinDate ?? apiUser.createdAt ?? '';
  // Normalise userEmail — detail endpoint may use email
  const email = apiUser.userEmail ?? apiUser.email ?? '';
  // Normalise role — detail endpoint may use roleName
  const role = apiUser.role ?? apiUser.roleName ?? primaryMapping?.role ?? '';

  // Prefer locations with activeStatus: true. If the backend marks every entry
  // as false (common for market/region-scoped roles where real data lives in
  // locationMappings), fall through to the locationMappings source instead of
  // returning an empty array.
  const activeLocationsFromArray = (apiUser.locations ?? []).filter((loc) => loc.activeStatus !== false);

  const mappedLocations = activeLocationsFromArray.length > 0
    ? activeLocationsFromArray
    : apiUser.locationMappings?.flatMap((mapping) => {
        // Note: liveFlag is not a reliable active/inactive indicator — newly
        // provisioned market/region scope mappings are returned with liveFlag
        // "No" even though they are the user's current, active assignments.
        // Do not use it to skip entries here.
        const liveFlag = String(mapping.liveFlag ?? '').trim().toLowerCase();

        const locationName = String(
          mapping.locationName ?? mapping.scopeMarket ?? mapping.scopeRegion ??
          mapping.location ?? mapping.locationCode ?? '',
        ).trim();

        if (!locationName) return [];

        return [{
          location: locationName,
          locationId: String(mapping.locationId ?? ''),
          activeStatus: liveFlag !== 'no' && liveFlag !== 'false',
        }];
      }) ??
    // Last resort: use unfiltered locations (at minimum shows something)
    (apiUser.locations ?? []);

  // GLOBAL-scoped roles (Super User, Developer, etc.) have access to every location —
  // show "All Locations" instead of an (often empty) per-location list. The list
  // endpoint returns scopeType at the top level; the detail endpoint (and some
  // rows) may only carry it inside locationMappings, or omit it entirely for
  // privileged roles created with roleId-only mappings (no scope data at all).
  const isGlobalScope =
    String(apiUser.scopeType ?? '').trim().toUpperCase() === 'GLOBAL' ||
    (apiUser.locationMappings ?? []).some((mapping) => {
      const rawScope = mapping.scopeType ?? (mapping as any).SCOPE_TYPE ?? (mapping as any).scope_type ?? '';
      return String(rawScope).trim().toUpperCase() === 'GLOBAL';
    }) ||
    role.trim().toLowerCase() === 'super user' ||
    role.trim().toLowerCase() === 'developer';

  const mappedSustainTeamMember = apiUser.sustainTeamMember ?? primaryMapping?.sustainTeamMember ?? null;
  const mappedFundamentals = apiUser.fundamentals ?? primaryMapping?.fundamentals ?? null;
  const mappedLighthouse = apiUser.lighthouse ?? primaryMapping?.lighthouse ?? null;
  const mappedAdvFundamentals = apiUser.advFundamentals ?? primaryMapping?.advFundamentals ?? null;
  const mappedActiveStatus = (() => {
    // 1. Top-level activeStatus field (returned by both list and detail endpoints)
    if (typeof apiUser.activeStatus === 'boolean') {
      return apiUser.activeStatus ? 'Active' : 'Inactive';
    }
    // 2. Per-location activeStatus (list endpoint fallback)
    const locationActive = apiUser.locations?.[0]?.activeStatus;
    if (typeof locationActive === 'boolean') {
      return locationActive ? 'Active' : 'Inactive';
    }
    // 3. liveFlag in locationMappings (legacy fallback)
    const liveFlag = String(primaryMapping?.liveFlag ?? '').trim().toLowerCase();
    if (liveFlag === 'yes' || liveFlag === 'true' || liveFlag === 'active') {
      return 'Active';
    }
    if (liveFlag === 'no' || liveFlag === 'false' || liveFlag === 'inactive') {
      return 'Inactive';
    }
    return 'Inactive';
  })();

  return {
    id: numericId != null ? String(numericId) : `__row_${index}`,
    userEmail: email,
    gpid: apiUser.gpid ?? '',
    organization: apiUser.organization ?? primaryMapping?.organizationType ?? '',
    department: apiUser.department ?? primaryMapping?.division ?? '',
    accessLevel: role,
    // 'All Locations' matches the reserved GLOBAL-scope display text; the location
    // filter (useUserManagementData.ts) matches on this same literal string.
    location: isGlobalScope ? ['All Locations'] : mappedLocations.map(loc => loc.location),
    locationId: mappedLocations[0]?.locationId || '',
    activeStatus: mappedActiveStatus,
    sustainTeamMember: mappedSustainTeamMember,
    sustainTeamRole: apiUser.sustainTeamRole ?? null,
    fundamentals: mappedFundamentals,
    lighthouse: mappedLighthouse,
    advFundamentals: mappedAdvFundamentals,
    newStatus: apiUser.newStatus ?? (apiUser as any).isNew ?? (apiUser as any).newUser ?? false,
    naviJoinDate: typeof joinDate === 'string' ? joinDate : '',
    lastUpdateTime: typeof lastUpdate === 'string' ? lastUpdate : '',
  };
}

/**
 * Transform frontend User model to backend DTO
 */
function mapUserToApi(user: User): any {
  const state = store.getState() as any;
  const updatedBy = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';

  const payload: any = {
    locations: user.location.map(loc => ({
      location: loc,
      activeStatus: user.activeStatus === 'Active',
    })),
    newStatus: user.newStatus,
  };

  if (updatedBy) {
    payload.updatedBy = updatedBy;
  }

  if (user.sustainTeamMember !== null && user.sustainTeamMember !== undefined) {
    payload.sustainTeamMember = user.sustainTeamMember;
    if (user.sustainTeamMember === true && user.sustainTeamRole) {
      payload.sustainTeamRole = user.sustainTeamRole;
    }
  }

  if (user.fundamentals !== undefined && user.fundamentals !== null) {
    payload.fundamentals = user.fundamentals;
  }
  if (user.lighthouse !== undefined && user.lighthouse !== null) {
    payload.lighthouse = user.lighthouse;
  }
  if (user.advFundamentals !== undefined && user.advFundamentals !== null) {
    payload.advFundamentals = user.advFundamentals;
  }

  return payload;
}

/**
 * User Management API Service
 */
export const userManagementApi = {
  /**
   * Get a single page of users.
   * GET /admin/users?page=0&pageSize=100[&search=...&sortBy=...&sortOrder=...&location=...&accessLevel=...&activeStatus=...]
   * Returns { users, hasMore, totalCount, totalPages, activeCount, newUserCount }.
   */
  getPage: async (
    page: number,
    pageSize: number,
    options?: {
      search?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      location?: string[];
      accessLevel?: string[];
      activeStatus?: string[];
    }
  ): Promise<{ users: User[]; hasMore: boolean; totalCount: number; totalPages: number; activeCount: number; newUserCount: number }> => {
    try {
      // Build query string without URLSearchParams for React Native compatibility
      // URLSearchParams is a Web API not available in React Native
      const queryParams: string[] = [
        `page=${page}`,
        `pageSize=${pageSize}`,
      ];
      
      if (options?.search?.trim()) {
        queryParams.push(`search=${encodeURIComponent(options.search.trim())}`);
      }
      if (options?.sortBy) {
        queryParams.push(`sortBy=${encodeURIComponent(options.sortBy)}`);
      }
      if (options?.sortOrder) {
        queryParams.push(`sortOrder=${encodeURIComponent(options.sortOrder)}`);
      }
      if (options?.location?.length) {
        options.location.forEach(l => {
          queryParams.push(`location=${encodeURIComponent(l)}`);
        });
      }
      if (options?.accessLevel?.length) {
        options.accessLevel.forEach(a => {
          queryParams.push(`accessLevel=${encodeURIComponent(a)}`);
        });
      }
      if (options?.activeStatus?.length) {
        options.activeStatus.forEach(s => {
          queryParams.push(`activeStatus=${encodeURIComponent(s)}`);
        });
      }

      const url = `/admin/users?${queryParams.join('&')}`;
      console.log('[UserManagementAPI] Calling GET', url);

      const response = await api.get(url);

      const responseData: BackendPagedResponse = response.data ?? {};

      // Extract the records array — try wrapped keys before falling back to a
      // bare array (legacy shape).
      const raw: BackendUserResponse[] =
        responseData.data ??
        responseData.content ??
        (Array.isArray(response.data) ? response.data : []);

      if (!Array.isArray(raw)) {
        console.error('[UserManagementAPI] Response is not an array!', typeof raw, raw);
        return { users: [], hasMore: false, totalCount: 0, totalPages: 0, activeCount: 0, newUserCount: 0 };
      }

      const users = raw.map((u, i) => mapApiToUser(u, i));

      // Prefer the backend's explicit hasNext flag; fall back to the
      // page-size heuristic only when the field is absent (older backends).
      const hasMore: boolean =
        typeof responseData.hasNext === 'boolean'
          ? responseData.hasNext
          : users.length === pageSize;

      const totalCount = responseData.totalCount ?? users.length;
      const totalPages = responseData.totalPages ?? (hasMore ? page + 2 : page + 1);
      const activeCount = responseData.activeCount ?? 0;
      const newUserCount = responseData.newUserCount ?? 0;

      console.log(
        `[UserManagementAPI] Page ${page}: received ${users.length} users,`,
        `totalCount=${totalCount}, totalPages=${totalPages}, hasNext=${hasMore},`,
        `activeCount=${activeCount}, newUserCount=${newUserCount}`
      );
      return { users, hasMore, totalCount, totalPages, activeCount, newUserCount };
    } catch (error: any) {
      console.error('[UserManagementAPI] getPage error:', error);
      throw new Error(
        error?.response?.data?.message ??
        error?.message ??
        'Failed to load users'
      );
    }
  },

  /**
   * Returns Active and New user counts for the AdminDashboard card.
   * A single GET ?page=0&pageSize=1 is enough — the backend now includes
   * activeCount and newUserCount directly in the response metadata.
   */
  getStats: async (): Promise<{ activeCount: number; newCount: number }> => {
    const response = await api.get(`/admin/users?page=0&pageSize=1`);
    const body = response.data ?? {};

    console.log('[UserManagementAPI] getStats raw response body:', JSON.stringify(body));

    const activeCount: number = body.activeCount ?? 0;
    const newCount: number = body.newUserCount ?? 0;

    console.log(`[UserManagementAPI] getStats resolved → activeCount=${activeCount}, newCount=${newCount}`);

    return { activeCount, newCount };
  },


  /**
   * Fetch ALL records matching a search term by walking every page until
   * hasNext is false. Used by the search bar so results are never truncated.
   */
  searchAll: async (search: string): Promise<User[]> => {
    const PAGE = 100;
    const accumulated: User[] = [];
    let page = 0;
    let more = true;

    while (more) {
      const { users: batch, hasMore } = await userManagementApi.getPage(page, PAGE, { search });
      accumulated.push(...batch);
      more = hasMore;
      page++;
    }

    console.log(`[UserManagementAPI] searchAll "${search}": total ${accumulated.length} records`);
    return accumulated;
  },

  /**
   * @deprecated Use getPage instead. Kept for refreshUsers calls after mutations.
   * Fetches only the first page (page 0) – sufficient for post-mutation refresh.
   */
  getAll: async (): Promise<User[]> => {
    const { users } = await userManagementApi.getPage(0, 100);
    return users;
  },

  /**
   * Get user by email
   * GET /admin/users/by-email?email={email}
   */
  getByEmail: async (email: string): Promise<User> => {
    try {
      const { data } = await api.get(`${API_BASE}/by-email`, {
        params: { email },
        _skipLocationContext: true,
      } as any);
      const user = data?.data ?? data?.user ?? data?.result ?? data;
      return mapApiToUser(user, 0);
    } catch (error: any) {
      console.error('[UserManagementAPI] getByEmail error:', error);
      throw new Error(
        error?.response?.data?.message ?? 
        error?.message ?? 
        'Failed to load user'
      );
    }
  },

  /**
   * Update user
   * PUT /admin/users/{email}
   */
  update: async (request: UserUpdateRequest): Promise<User> => {
    try {
      const { userEmail, user, payload } = request;
      const body: UserUpdatePayload = {
        ...payload,
        updatedBy: payload.updatedBy ?? (store.getState() as any).appData?.userInfo?.email ?? (store.getState() as any).auth?.user?.email ?? (store.getState() as any).auth?.user?.username ?? '',
      };
      
      console.log('[UserManagementAPI] Updating user email:', userEmail);
      console.log('[UserManagementAPI] Payload being sent:', JSON.stringify(body, null, 2));
      
      const { data } = await api.put(`${API_BASE}/${encodeURIComponent(userEmail)}`, body);
      
      const updatedUser = data?.data || data;

      // Only map the API response if it came back with a valid id. Some
      // backends return 200 with an empty body or just {}. In that case
      // fall back to the frontend user object (which has all the edits).
      if (updatedUser && updatedUser.id != null) {
        return mapApiToUser(updatedUser, 0);
      }
      return user;
    } catch (error: any) {
      console.error('[UserManagementAPI] update error:', error);
      console.error('[UserManagementAPI] update error response:', error?.response?.data);
      throw new Error(
        error?.response?.data?.message ?? 
        error?.message ?? 
        'Failed to update user'
      );
    }
  },

  /**
   * Bulk update training dates
   * PUT /admin/users/bulk-training
   */
  bulkUpdateTraining: async (
    emails: string[],
    fundamentals: string | null,
    lighthouse: string | null,
    advFundamentals: string | null,
    newStatus: boolean
  ): Promise<void> => {
    try {
      const payload = {
        emails,
        fundamentals,
        lighthouse,
        advFundamentals,
        newStatus,
      };
      
      console.log('[UserManagementAPI] Bulk training update payload:', payload);
      
      await api.put(`${API_BASE}/bulk-training`, payload);
    } catch (error: any) {
      console.error('[UserManagementAPI] bulkUpdateTraining error:', error);
      throw new Error(
        error?.response?.data?.message ?? 
        error?.message ?? 
        'Failed to update training dates'
      );
    }
  },

  /**
   * Clear new tags for users
   * PUT /admin/users/bulk-training with newStatus=false
   */
  clearNewTags: async (emails: string[]): Promise<void> => {
    try {
      const payload = {
        emails,
        fundamentals: null,
        lighthouse: null,
        advFundamentals: null,
        newStatus: false,
      };
      
      console.log('[UserManagementAPI] Clear new tags payload:', payload);
      
      await api.put(`${API_BASE}/bulk-training`, payload);
    } catch (error: any) {
      console.error('[UserManagementAPI] clearNewTags error:', error);
      throw new Error(
        error?.response?.data?.message ?? 
        error?.message ?? 
        'Failed to clear new tags'
      );
    }
  },

  /**
   * Get full user data by numeric ID
   * GET /admin/users/{userId}
   */
  getById: async (id: string): Promise<User> => {
    try {
      const { data } = await api.get(`${API_BASE}/${encodeURIComponent(id)}`);
      // Handle multiple response wrapper shapes from the detail endpoint
      const user = data?.data ?? data?.user ?? data?.result ?? data;
      return mapApiToUser(user, 0);
    } catch (error: any) {
      console.error('[UserManagementAPI] getById error:', error);
      throw new Error(
        error?.response?.data?.message ??
        error?.message ??
        'Failed to load user'
      );
    }
  },

  /**
   * Remove all Navigator access for a user
   * DELETE /admin/users/by-email?email={email}
   */
  removeAccess: async (email: string): Promise<void> => {
    try {
      await api.delete(`${API_BASE}/by-email`, {
        params: { email },
        _skipLocationContext: true,
      } as any);
    } catch (error: any) {
      console.error('[UserManagementAPI] removeAccess error:', error);
      throw new Error(
        error?.response?.data?.message ??
        error?.message ??
        'Failed to remove user access'
      );
    }
  },
};
